<template>
  <section v-if="pricingPlans.length" id="pricing" class="relative overflow-visible bg-slate-25 section-padding">
  <div class="absolute inset-x-0 top-0 -z-10 h-64 bg-gradient-to-b from-hilab/10 via-transparent to-transparent"></div>
  <div class="container-full-width">
    <div class="mx-auto max-w-3xl text-center">
      <span v-if="badge" class="badge">{{ badge }}</span>
      <h2 v-if="title" class="section-title mt-6">{{ title }}</h2>
      <p v-if="heroDescription" class="section-subtitle mx-auto">
        {{ heroDescription }}
      </p>
    </div>
      <div v-if="pricingNote" class="section-note"><p>{{ pricingNote }}</p></div>
      <div class="pricing-grid grid gap-6 sm:grid-cols-2 lg:grid-cols-4 justify-center overflow-visible pt-6">
        <article
          v-for="plan in pricingPlans"
          :key="plan.name"
           :class="[
             'group relative flex flex-col h-full rounded-3xl border bg-white/95 shadow-sm pricing-card pt-10 px-8 pb-8 scroll-scale-up overflow-visible',
             plan.badge ? 'gradient-border border-transparent popular' : 'border-slate-100',
           ]"
        >
          <!-- Badge -->
          <div v-if="plan.badge" class="absolute -top-4">
            <span class="inline-flex items-center px-4 py-1.5 rounded-full text-xs font-bold text-white bg-gradient-to-r from-hilab to-hilab/80 shadow-lg border border-hilab/20">
              {{ plan.badge }}
            </span>
          </div>

          <!-- Header -->
          <div class="pb-6 text-center">
            <h3 class="text-lg font-semibold text-slate-900 md:text-xl">{{ plan.name }}</h3>
            <p class="mt-1 text-sm text-slate-500">{{ plan.subtitle }}</p>
          </div>

          <!-- Pricing -->
          <div class="pb-6">
            <div class="text-center">
              <!-- Yearly Price (Primary) -->
              <div v-if="hasYearly(plan)" class="mb-4">
                <div class="flex items-baseline justify-center gap-1">
                  <span v-if="plan.priceYearlyCurrency" class="text-base font-medium text-slate-500">{{ plan.priceYearlyCurrency }}</span>
                  <span class="text-3xl font-bold tracking-tight text-slate-900 whitespace-nowrap">{{ plan.priceYearly }}</span>
                  <span v-if="plan.priceYearlySuffix" class="text-sm font-medium text-slate-500">{{ plan.priceYearlySuffix }}</span>
                </div>
                <p class="mt-1 text-[11px] uppercase tracking-wider text-slate-400">Billed annually</p>
                <p v-if="plan.yearlyNote" class="mt-2 inline-block rounded-full bg-gradient-to-r from-hilab/10 to-hilab/5 px-4 py-1.5 text-[11px] font-semibold text-hilab border border-hilab/20">
                  {{ plan.yearlyNote }}
                </p>
              </div>

              <!-- Monthly Price (Secondary) -->
              <div v-if="hasMonthly(plan)" :class="['pt-4', hasYearly(plan) ? 'border-t border-slate-100' : '']">
                <div class="flex items-baseline justify-center gap-1">
                  <span v-if="plan.priceMonthlyCurrency" class="text-sm font-medium text-slate-500">{{ plan.priceMonthlyCurrency }}</span>
                  <span class="text-lg font-semibold text-slate-700">{{ plan.priceMonthly }}</span>
                  <span v-if="plan.priceMonthlySuffix" class="text-xs font-medium text-slate-500">{{ plan.priceMonthlySuffix }}</span>
                </div>
                <p class="mt-1 text-[11px] uppercase tracking-wider text-slate-400">Monthly billing</p>
              </div>
            </div>
          </div>

          <!-- Description -->
          <div class="pb-6">
            <p class="text-sm leading-relaxed text-slate-600 text-center">{{ plan.description }}</p>
          </div>

          <!-- Features -->
          <div class="flex-1 pb-6">
            <ul class="space-y-3">
              <li v-for="feature in plan.features" :key="feature" class="flex items-start gap-3">
                <div class="flex-shrink-0 mt-0.5">
                  <LucideIcon name="check-check" :size="16" class="text-hilab" />
                </div>
                <span class="text-sm text-slate-600 leading-relaxed">{{ feature }}</span>
              </li>
            </ul>
          </div>

          <!-- CTA Button -->
          <div class="pt-0">
            <a
              v-if="plan.cta_type !== 'secondary'"
              :href="primaryCtaUrl"
              :class="[
                'group relative block w-full rounded-xl py-3 px-4 text-center text-sm font-semibold transition-all duration-300 overflow-hidden',
                plan.badge 
                  ? 'bg-gradient-to-r from-hilab to-hilab/90 text-white hover:from-hilab-dark hover:to-hilab shadow-lg hover:shadow-xl hover:scale-105' 
                  : 'bg-gradient-to-r from-slate-900 to-slate-800 text-white hover:from-slate-800 hover:to-slate-700 shadow-md hover:shadow-lg hover:scale-105'
              ]"
            >
              <span class="relative z-10">Bắt đầu ngay</span>
              <div class="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700"></div>
            </a>
            <button
              v-else
              type="button"
              class="group relative block w-full rounded-xl border border-slate-300 bg-white py-3 px-4 text-center text-sm font-semibold text-slate-700 transition-all duration-300 hover:border-hilab/30 hover:bg-hilab/5 hover:text-hilab overflow-hidden"
              @click="handleSecondaryClick(plan)"
            >
              <span class="relative z-10">Liên hệ tư vấn</span>
              <div class="absolute inset-0 bg-gradient-to-r from-hilab/5 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-500"></div>
            </button>
          </div>
        </article>
      </div>
      <div v-if="cta" class="mt-12 rounded-3xl border border-hilab/20 bg-white/80 p-8 text-sm text-slate-500 shadow-lg">
        <div class="flex flex-col gap-6 md:flex-row md:items-center md:justify-between">
          <div>
            <p v-if="cta.title" class="text-lg font-semibold text-slate-900">{{ cta.title }}</p>
            <p v-if="cta.description" class="mt-2 text-sm text-slate-500">{{ cta.description }}</p>
          </div>
          <a v-if="cta.label" :href="cta.url || primaryCtaUrl" class="primary-button">{{ cta.label }}</a>
        </div>
      </div>
  </div>
</section>
</template>

<script setup>
import { computed } from 'vue';
import LucideIcon from '../../dashboard/components/LucideIcon.vue';

const emit = defineEmits(['request-consultation']);

const props = defineProps({
  primaryCtaUrl: {
    type: String,
    required: true,
  },
  content: {
    type: Object,
    default: () => ({}),
  },
});

const sectionContent = computed(() => props.content || {});
const badge = computed(() => sectionContent.value.badge || '');
const title = computed(() => sectionContent.value.title || '');
const pricingNote = computed(() => sectionContent.value.note || '');
const heroDescription = computed(() => sectionContent.value.description || '');
const rawPlans = computed(() => (Array.isArray(sectionContent.value.plans) ? sectionContent.value.plans : []));
const normalizeType = t => (typeof t === 'string' ? t.trim().toLowerCase() : '');

const pricingPlans = computed(() =>
  rawPlans.value
    .map(plan => ({
      name: plan.name,
      subtitle: plan.subtitle,
      badge: plan.badge,
    priceYearlyCurrency: plan.price_yearly_currency,
    priceYearly: plan.price_yearly,
    priceYearlySuffix: plan.price_yearly_suffix,
    priceMonthlyCurrency: plan.price_monthly_currency,
    priceMonthly: plan.price_monthly,
    priceMonthlySuffix: plan.price_monthly_suffix,
    yearlyNote: plan.yearly_note,
      description: plan.description,
      features: plan.features || [],
      cta_type: normalizeType(plan.cta_type),
      cta_url: plan.cta_url,
    }))
    .filter(plan => plan.name)
);

const cta = computed(() => {
  const data = sectionContent.value.cta;
  if (!data) {
    return null;
  }
  if (data.title || data.description || data.label) {
    return data;
  }
  return null;
});

// Helpers
const hasYearly = plan => Boolean(plan.priceYearly)
const hasMonthly = plan => Boolean(plan.priceMonthly)

const handleSecondaryClick = plan => {
  const planName = plan?.name ? plan.name : '';
  emit('request-consultation', planName ? `pricing:${planName}` : 'pricing');
};
</script>
